import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  model,
  OnDestroy,
  Signal,
} from '@angular/core'
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTooltip } from '@angular/material/tooltip'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderNumberCellDirective,
  ElderQuantityPipe,
  ElderSelectComponent,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { filter, mergeMap } from 'rxjs'
import { Package } from '../../../../packages/package'
import { PackageFilterBuilder } from '../../../../packages/package-filter-builder'
import { PackageProductionLinePath, PackageService } from '../../../../packages/package.service'
import { truthyPredicate } from '../../../../../../functional.util'
import {
  ProductionLinePathComponent,
  ProductionLinePaths,
} from '../../../../manufacturing/production-lines/production-line-path/production-line-path.component'
import {
  TerminalOrderPackageDetailDialogueComponent,
  TerminalOrderPackageDialogueOptions,
} from '../terminal-orders-package-detail/terminal-order-package-detail-dialogue/terminal-order-package-detail-dialogue.component'

@Component({
  selector: 'app-terminal-order-packages',
  templateUrl: './terminal-order-packages.component.html',
  imports: [
    FormsModule,
    ElderTranslatePipe,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSlideToggle,
    MatFormField,
    MatLabel,
    MatSortHeader,
    MatTooltip,
    ElderQuantityPipe,
    ElderDataToolbarComponent,
    ElderTableToolbarDirective,
    ElderTableComponent,
    ElderToolbarContentDirective,
    ElderNumberCellDirective,
    ElderSelectComponent,
    ProductionLinePathComponent,
  ],
})
export class TerminalOrderPackagesComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  public readonly data: IDataContext<Package>
  public readonly workOrderId = input.required<string>()
  public readonly workCenterId = input<string>()
  readonly showAllWorkCenters = model(false)
  private dialog = inject(MatDialog)
  readonly productionLinePaths: Signal<PackageProductionLinePath[]>
  readonly selectedProductionLinePath = model<PackageProductionLinePath | null>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const packageService = inject(PackageService)
    this.data = DataContextBuilder.start<Package>()
      .pageSize(30)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll([PackageFilterBuilder.WORK_ORDER_ID], [new Sort('number', 'asc')])
      .build(packageService)

    effect(() => {
      const showAll = this.showAllWorkCenters()
      if (showAll) {
        this.data.filter.replaceFilters([this.defaultFilter(this.workOrderId())])
      } else if (this.workCenterId) {
        this.log.debug(`Updating filter with workCenter ${this.workCenterId()}`)
        this.selectedProductionLinePath.set(null)
        this.data.filter.updateFilter(
          PackageFilterBuilder.productionPathWorkCenter(this.workCenterId())
        )
      }
    })

    effect(() => {
      if (this.workOrderId()) {
        this.log.debug(`Updating filter with workOrderId ${this.workOrderId()}`)
        this.data.filter.updateFilter(this.defaultFilter(this.workOrderId()))
      }
    })

    effect(() => {
      if (this.selectedProductionLinePath()) {
        const path = this.selectedProductionLinePath().path
        this.log.debug(`Updating filter with selectedProductionLinePath ${path}`)
        this.data.filter.updateFilter(PackageFilterBuilder.productionPath(path))
      } else {
        this.data.filter.removeFilter(PackageFilterBuilder.PRODUCTION_PATH)
      }
    })

    const productionLinePaths$ = toObservable(this.workOrderId).pipe(
      filter(truthyPredicate),
      takeUntilDestroyed(),
      mergeMap((workOrderId) => packageService.fetchProductionLines(workOrderId))
    )
    this.productionLinePaths = toSignal(productionLinePaths$)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    this.data.close()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  openDetail(pkg?: Package): void {
    if (pkg) {
      this.dialog.open(TerminalOrderPackageDetailDialogueComponent, {
        maxWidth: '95vw',
        maxHeight: '95vw',
        width: '95vw',
        height: '95vh',
        data: <TerminalOrderPackageDialogueOptions>{
          pkgId: pkg.id,
        },
      })
    }
  }

  getDisplay(productionLinePath: string): string {
    return this.productionLinePaths().find((pl) => pl.path === productionLinePath)?.display
  }

  getProductionLinePaths(
    productionLinePath: string,
    involvedProductionLinePaths: PackageProductionLinePath[]
  ): ProductionLinePaths {
    const pl = involvedProductionLinePaths.find((pl) => pl.path === productionLinePath)
    const wcs = pl?.workCenterSequence ?? []
    return <ProductionLinePaths>{ workCenterNumbers: wcs.map((wc) => wc.number) }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private defaultFilter(workOrderId: string): Filter {
    return PackageFilterBuilder.workOrderId(workOrderId)
  }
}
